import React, { useId } from 'react';
import { imgSrc } from './tiendaUtils';

const canal = (hex, i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
const mezclar = (hex, otro, t) => `#${[0, 1, 2].map(i => Math.round(canal(hex, i) * (1 - t) + canal(otro, i) * t).toString(16).padStart(2, '0')).join('')}`;

// Tela elegida ampliada (esquina superior izquierda: las muestras traen un recuadro de detalle abajo a la derecha)
const Tela = ({ url }) => (url
  ? <image href={imgSrc(url)} x="0" y="0" width="800" height="600" preserveAspectRatio="xMinYMin slice" />
  : <rect width="400" height="300" fill="#F2EFE9" />);

const Boton = ({ x, y, r = 13 }) => (
  <g>
    <circle cx={x} cy={y + 2} r={r} fill="rgba(0,0,0,0.28)" />
    <circle cx={x} cy={y} r={r} fill="url(#boton-nacar)" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8" />
    <circle cx={x} cy={y} r={r - 3.5} fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="0.8" />
    {[[-3.5, -3.5], [3.5, -3.5], [-3.5, 3.5], [3.5, 3.5]].map(([dx, dy]) => <circle key={`${dx}${dy}`} cx={x + dx} cy={y + dy} r="1.8" fill="rgba(0,0,0,0.5)" />)}
  </g>
);

const Pecho = () => (
  <g>
    <rect width="400" height="300" fill="url(#luz-pecho)" />
    <path d="M0 210 C120 180 200 230 290 200 L290 230 C200 260 110 210 0 245 Z" fill="rgba(0,0,0,0.06)" filter="url(#pliegue)" />
    <rect x="292" y="0" width="56" height="300" fill="rgba(255,255,255,0.16)" />
    <rect x="280" y="0" width="12" height="300" fill="url(#sombra-tapeta)" />
    <rect x="348" y="0" width="5" height="300" fill="rgba(0,0,0,0.2)" />
    {[300, 340].map(x => <line key={x} x1={x} y1="0" x2={x} y2="300" stroke="rgba(0,0,0,0.38)" strokeWidth="1.2" strokeDasharray="5 3.5" />)}
    <Boton x={320} y={62} />
    <Boton x={320} y={226} />
  </g>
);

const Puno = () => (
  <g>
    <rect width="400" height="150" fill="url(#sombra-manga)" />
    <path d="M150 0 C160 60 175 110 190 150 M250 0 C246 60 238 110 232 150" fill="none" stroke="rgba(0,0,0,0.16)" strokeWidth="3" />
    <rect x="14" y="150" width="372" height="118" rx="16" fill="rgba(255,255,255,0.2)" stroke="rgba(0,0,0,0.38)" strokeWidth="1.5" />
    <rect x="14" y="148" width="372" height="12" fill="url(#sombra-costura)" />
    <rect x="24" y="160" width="352" height="98" rx="11" fill="none" stroke="rgba(0,0,0,0.34)" strokeWidth="1.2" strokeDasharray="5 3.5" />
    <rect x="0" y="268" width="400" height="32" fill="url(#sombra-mano)" />
    <Boton x={344} y={209} r={13} />
  </g>
);

const POSICION = { pecho: { x: 150, y: 172, size: 46 }, puno: { x: 170, y: 224, size: 42 } };

export default function VistaBordado({ texto, placeholder, ubicacion, color, tela, telaNombre }) {
  const id = useId().replace(/:/g, '');
  const hex = color?.hex || '#1F2A44';
  const lugar = ubicacion?.id === 'puno' ? 'puno' : 'pecho';
  const p = POSICION[lugar];
  const vacio = !texto;
  return (
    <figure className="space-y-2" data-testid="bordado-vista">
      <div className="overflow-hidden rounded-2xl border border-[#27272A] shadow-lg">
        <svg viewBox="0 0 400 300" className="block h-auto w-full" role="img" aria-label={`Vista de las iniciales ${texto || ''} en ${ubicacion?.titulo || 'la camisa'}, hilo ${color?.nombre || ''}`}>
          <defs>
            <radialGradient id="luz-pecho" cx="35%" cy="30%" r="80%">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.16" />
              <stop offset="60%" stopColor="#fff" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.22" />
            </radialGradient>
            <linearGradient id="sombra-manga" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#000" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.04" />
            </linearGradient>
            <linearGradient id="sombra-tapeta" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.38" />
            </linearGradient>
            <linearGradient id="sombra-costura" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#000" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#000" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="sombra-mano" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1c1917" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#1c1917" stopOpacity="0.85" />
            </linearGradient>
            <filter id="pliegue" x="-10%" y="-50%" width="120%" height="200%"><feGaussianBlur stdDeviation="9" /></filter>
            <radialGradient id="boton-nacar" cx="38%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#DCD6CB" />
            </radialGradient>
            <pattern id={`satin-${id}`} width="2.4" height="2.4" patternUnits="userSpaceOnUse" patternTransform="rotate(38)">
              <rect width="2.4" height="2.4" fill={hex} />
              <rect width="0.9" height="2.4" fill={mezclar(hex, '#FFFFFF', 0.28)} />
            </pattern>
            <filter id={`relieve-${id}`} x="-10%" y="-30%" width="120%" height="160%">
              <feDropShadow dx="0.7" dy="1.3" stdDeviation="0.8" floodColor="#000" floodOpacity="0.45" />
            </filter>
          </defs>
          <Tela url={tela} />
          {lugar === 'pecho' ? <Pecho /> : <Puno />}
          <text x={p.x} y={p.y} textAnchor="middle" fontFamily="Cinzel, 'Playfair Display', Georgia, serif" fontWeight="600"
            fontSize={p.size} letterSpacing="4" fill={`url(#satin-${id})`} stroke={mezclar(hex, '#000000', 0.35)} strokeWidth="0.6"
            filter={`url(#relieve-${id})`} opacity={vacio ? 0.35 : 1} data-testid="bordado-vista-texto">
            {texto || placeholder}
          </text>
        </svg>
      </div>
      <figcaption className="text-center text-xs text-zinc-500">
        Vista ilustrativa{telaNombre ? ` sobre ${telaNombre}` : ''}. El bordado real puede variar levemente.
      </figcaption>
    </figure>
  );
}
